// Dev-mode identity switcher: upn + roles, stored in localStorage and sent as x-dev-* headers.

import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { ALL_ROLES, type Role } from '../api.ts';
import { DEFAULT_DEV_IDENTITY, getDevIdentity, setDevIdentity } from '../auth.ts';
import { Icon } from './Icon.tsx';

export function DevSwitcher({ onChange, startOpen = false }: { onChange: () => void; startOpen?: boolean }) {
  const [open, setOpen] = useState(startOpen);
  const [upn, setUpn] = useState(() => getDevIdentity().upn);
  const [roles, setRoles] = useState<Role[]>(() => getDevIdentity().roles);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const id = useId();

  useEffect(() => {
    if (!open) return;
    const cur = getDevIdentity();
    setUpn(cur.upn);
    setRoles(cur.roles);
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && e.target instanceof Node && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  function apply(e: FormEvent) {
    e.preventDefault();
    setDevIdentity({ upn: upn.trim() || DEFAULT_DEV_IDENTITY.upn, roles });
    setOpen(false);
    onChange();
  }

  function reset() {
    setDevIdentity(null);
    setOpen(false);
    onChange();
  }

  const toggle = (r: Role) => setRoles((cur) => (cur.includes(r) ? cur.filter((x) => x !== r) : [...cur, r]));

  return (
    <div className="devsw" ref={boxRef}>
      <button type="button" className="btn sm dev" aria-expanded={open} aria-controls={`${id}-pop`} onClick={() => setOpen((o) => !o)} title="Switch dev identity">
        <Icon name="user" />
        Dev user
      </button>
      {open && (
        <form className="pop" id={`${id}-pop`} onSubmit={apply} aria-label="Dev identity">
          <div className="lbl">Dev identity · local devstack only</div>
          <label className="field">
            <span className="lbl">UPN</span>
            <input value={upn} onChange={(e) => setUpn(e.target.value)} spellCheck={false} autoComplete="off" autoFocus />
          </label>
          <fieldset className="field">
            <legend className="lbl">Roles</legend>
            <div className="roles">
              {ALL_ROLES.map((r) => (
                <label key={r} className="ck-inline">
                  <input type="checkbox" checked={roles.includes(r)} onChange={() => toggle(r)} />
                  {r}
                </label>
              ))}
            </div>
          </fieldset>
          <div className="row-end">
            <button type="button" className="btn" onClick={reset}>
              Reset to default
            </button>
            <button type="submit" className="btn pri">
              Apply
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
